import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Stack } from '@startuptools/ui/Stack';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Input } from '@startuptools/ui/Input';
import { DateTimeInput } from '@startuptools/ui/DateTimeInput';
import { DateInput } from '@startuptools/ui/DateInput';
import { TimeInput } from '@startuptools/ui/TimeInput';
import {
  type GqlCompany,
  type GqlMeeting,
  GqlMeetingHeldKind,
  GqlMeetingKind,
} from '../../../../graphql/base-types.graphql';
import { FiscalYearDisplay } from '@startuptools/business-ui/FiscalYearDisplay';
import { Option } from '@startuptools/ui/Option';
import { Select } from '@startuptools/ui/Select';
import { isNil } from 'lodash-es';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import { useMemo, useState, type ReactNode } from 'react';
import { useMeetingDetailsFormSuspenseQuery } from './MeetingDetailsForm.graphql';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { agmPeriod, getAgmSameYear, isBetween } from '../../../../helpers/agm';
import { isPromise } from '../../../../helpers/is-promise';

export type FormValues = {
  heldKind: GqlMeetingHeldKind;
  number: string | null;
  startTime: DateTime;
  endTime: DateTime | null;
  place: string | null;
  phone: string | null;
  meetingTemplateId: string | null;
};

export const MeetingDetailsForm = ({
  meetingKind,
  usesStepper,
  onSubmit,
  company,
  formDefaults,
  isEditing,
  meetings,
  actions,
}: {
  meetingKind: GqlMeetingKind;
  usesStepper: boolean;
  onSubmit: SubmitHandler<FormValues>;
  company: Pick<GqlCompany, 'id' | 'address' | 'fiscalYear'>;
  formDefaults: Omit<FormValues, 'meetingTemplateId'>;
  isEditing: boolean;
  meetings: Pick<GqlMeeting, 'id' | 'startTime' | 'kind'>[];
  actions: (loading: boolean) => ReactNode;
}) => {
  const { i18n } = useLingui();
  const [loading, setLoading] = useState(false);
  const { data } = useMeetingDetailsFormSuspenseQuery({
    variables: { companyId: company.id },
    fetchPolicy: 'cache-and-network',
    skip: isEditing || meetingKind !== GqlMeetingKind.BoardMeeting,
  });

  const { control, register, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      ...formDefaults,
      meetingTemplateId:
        meetingKind === GqlMeetingKind.BoardMeeting
          ? (data?.meetingAgendaTemplates.find(mat => mat.isDefault)?.id ?? data?.meetingAgendaTemplates[0]?.id ?? null)
          : null,
    },
  });
  const heldKind = watch('heldKind');
  const startTime = watch('startTime');
  const endTime = watch('endTime');

  const agmExists = useMemo(() => {
    if (meetingKind === GqlMeetingKind.ShareholdersMeeting) {
      return Boolean(getAgmSameYear(meetings, company.fiscalYear, startTime));
    }
    return false;
  }, [meetingKind, startTime, company.fiscalYear, meetings]);

  const outsidePeriod = useMemo(() => {
    if (meetingKind === GqlMeetingKind.ShareholdersMeeting) {
      return !isBetween(agmPeriod(company.fiscalYear, startTime), startTime);
    }
    return false;
  }, [meetingKind, company.fiscalYear, startTime]);

  const handleOnSubmit: SubmitHandler<FormValues> = async (values, event) => {
    setLoading(true);
    const res = onSubmit(values, event);
    if (isPromise(res)) {
      await res;
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      <Stack gap={2} flexGrow={1} width={488}>
        <Controller
          control={control}
          name="heldKind"
          render={({ field: { onChange, ...field } }) => (
            <FormControl required>
              <Select {...field} onChange={(_, value) => onChange(value)}>
                <Option value={GqlMeetingHeldKind.Physical}>
                  <Trans id="Physical" />
                </Option>
                <Option value={GqlMeetingHeldKind.PhysicalAndPhone}>
                  <Trans id="Physical and Phone/Video" />
                </Option>
                <Option value={GqlMeetingHeldKind.Phone}>
                  <Trans id="Phone/Video" />
                </Option>
                <Option value={GqlMeetingHeldKind.PerCapsulam}>
                  <Trans id="Per Capsulam" />
                </Option>
              </Select>
            </FormControl>
          )}
        />

        {meetingKind === GqlMeetingKind.BoardMeeting && (
          <FormControl required>
            <FormLabel>
              <Trans id="Meeting Number" />
            </FormLabel>
            <Input {...register('number')} />
          </FormControl>
        )}

        <Stack direction="row" gap={1}>
          {heldKind !== GqlMeetingHeldKind.PerCapsulam ? (
            <>
              <Controller
                control={control}
                name="startTime"
                render={({ field: { onChange, value, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Meeting starts at" />
                    </FormLabel>
                    <DateTimeInput
                      {...field}
                      value={value}
                      onChange={newValue => {
                        if (
                          !isNil(newValue) &&
                          !isNil(value) &&
                          !isNil(endTime) &&
                          value.toMillis() !== newValue.toMillis()
                        ) {
                          const diff = newValue.diff(value);
                          setValue('endTime', endTime.plus(diff));
                        }
                        onChange(newValue);
                      }}
                      sx={{ maxWidth: 300 }}
                    />
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="endTime"
                rules={{
                  validate: {
                    minDateTime: (value, formValues) => {
                      if (isNil(value) || isNil(formValues.startTime)) {
                        return true;
                      }
                      return formValues.startTime.toMillis() < value.toMillis();
                    },
                  },
                }}
                render={({ field: { onChange, value, ...field }, fieldState }) => (
                  <FormControl error={fieldState.invalid} color={fieldState.invalid ? 'danger' : 'neutral'}>
                    <FormLabel>
                      <Trans id="Meeting ends at" />
                    </FormLabel>
                    <TimeInput
                      {...field}
                      value={value}
                      onChange={newValue => {
                        if (isNil(value) && !isNil(newValue)) {
                          return onChange(startTime.set({ hour: newValue.hour, minute: newValue.minute }));
                        }
                        return onChange(newValue);
                      }}
                      sx={{ maxWidth: 180 }}
                    />
                  </FormControl>
                )}
              />
            </>
          ) : (
            <Controller
              control={control}
              name="startTime"
              render={({ field }) => (
                <FormControl required>
                  <FormLabel>
                    <Trans id="Meeting Date" />
                  </FormLabel>
                  <DateInput {...field} sx={{ maxWidth: 300 }} />
                </FormControl>
              )}
            />
          )}
        </Stack>

        {agmExists && (
          <Alert>
            <Box>
              <Trans
                id="There is already an annual general meeting at the period <dateFormat>{date}</dateFormat>"
                components={{
                  dateFormat: <DateFormat locale={i18n.locale} format={DateTime.DATE_SHORT} />,
                }}
                values={{ date: startTime.toISO() }}
              />
            </Box>
          </Alert>
        )}
        {outsidePeriod && (
          <Alert>
            <p style={{ margin: 0 }}>
              <Trans
                id="The selected date is outside of the six month period after the previous fiscal year {fiscalYear}"
                values={{ fiscalYear: <FiscalYearDisplay fiscalYear={company.fiscalYear} /> }}
              />
            </p>
          </Alert>
        )}

        {usesStepper && (
          <>
            {(heldKind === GqlMeetingHeldKind.Physical || heldKind === GqlMeetingHeldKind.PhysicalAndPhone) && (
              <FormControl required>
                <FormLabel>
                  <Trans id="Place/Address" />
                </FormLabel>
                <Input {...register('place')} />
              </FormControl>
            )}
            {(heldKind === GqlMeetingHeldKind.Phone || heldKind === GqlMeetingHeldKind.PhysicalAndPhone) && (
              <FormControl required>
                <FormLabel>
                  <Trans id="Phone/Video" />
                </FormLabel>
                <Input {...register('phone')} />
              </FormControl>
            )}

            {!isEditing && meetingKind === GqlMeetingKind.BoardMeeting && (
              <Controller
                control={control}
                name="meetingTemplateId"
                render={({ field: { onChange, ...field } }) => (
                  <FormControl required>
                    <FormLabel required>
                      <Trans id="Template" />
                    </FormLabel>
                    <Select {...field} onChange={(_, value) => onChange(value)}>
                      {data?.meetingAgendaTemplates?.map(mat => (
                        <Option key={mat.id} value={mat.id}>
                          {mat.name}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            )}
          </>
        )}
        {actions(loading)}
      </Stack>
    </form>
  );
};
